/**
 * The one workspace sidebar (Isaiah Cotto, 2026-10-14: "the shadcn side panel instead of the views at the top";
 * 2026-10-30: "I want sidebar to be standard thru out"). Every workforce shell and the admin console mount this, built
 * on the shadcn Sidebar (components/shadcn/ui/sidebar.tsx): provider, collapsible-to-icon rail, an in-place modal sheet
 * below 768px, grouped menu, header trigger. Why the shadcn file departs from the generated one is written at its top.
 *
 *   <WorkspaceMenu current="UMED" />
 *   <WorkspaceLayout workspace="UMED" navLabel="UMED views" groups={groups}>
 *     <header>… <SidebarTrigger /> …</header>
 *     <main>…</main>
 *   </WorkspaceLayout>
 *
 * - Every item is a real link (`href`), so a view is deep-linkable, opens in a new tab and survives a refresh; the
 *   host's own URL rules build the href. `active` is the host's word (it knows its view); the item says so with
 *   aria-current="page".
 * - A count is a badge; `badge.label` is what a screen reader hears ("3 blocked"); the bare number otherwise.
 * - Desktop (768px and up): the rail collapses to icons and back; the choice is remembered on this device.
 * - The header names the workspace. In the operations workspaces (UMED, Scheduling, Finance) it is also a switcher among
 *   the ones the server's index says the person can open, never one they cannot; elsewhere the portal's top bar is the
 *   switcher.
 * - Navigation only. Nothing here reads or writes a record.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  BarChart3, Building2, CalendarDays, Check, ChevronsUpDown, ClipboardList, FileCheck2, HeartPulse, Landmark, Phone,
  ShieldCheck, Stethoscope, Workflow, type LucideIcon,
} from 'lucide-react';
import type { WorkspaceKey } from '../../services/workspacesApi';
import {
  Sidebar, SidebarContent, SidebarFooter, SidebarGroup, SidebarGroupLabel, SidebarHeader, SidebarInset, SidebarMenu,
  SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem, SidebarProvider, SidebarTrigger as ShadcnSidebarTrigger, useSidebar,
  SIDEBAR_STORAGE_KEY, SIDEBAR_MOBILE_QUERY, type SidebarContextProps,
} from '../shadcn/ui/sidebar';
import { cn } from '../shadcn/lib/utils';
import { ThemeSwitch } from '../theme';
import { OPS_WORKSPACES, useOpsWorkspaces, type OpsWorkspace, type OpsWorkspaceKey } from './useOpsWorkspaces';
import './workspaceSidebar.css';

export { SIDEBAR_STORAGE_KEY, SIDEBAR_MOBILE_QUERY };

export interface SidebarBadge {
  value: number;
  /** What a screen reader hears instead of the bare number ("3 blocked"). */
  label?: string;
  tone?: 'default' | 'stop';
}

export interface SidebarItem {
  key: string;
  label: string;
  icon: LucideIcon;
  /** The view's own URL (the host's URL rules), so the link deep-links. */
  href: string;
  active?: boolean;
  badge?: SidebarBadge | null;
  /** A second count beside the first, in its own tone (Scheduling's ATD "not planned"; UMED's "returned to you"). */
  secondary?: SidebarBadge | null;
  /** A longer explanation (tooltip). */
  title?: string;
  /** Replace the history entry instead of pushing one (Scheduling's view changes always have). */
  replace?: boolean;
  /** Called on a click, before the router navigates (e.g. clear a notice that belongs to the old view). */
  onSelect?: () => void;
}

export interface SidebarGroup { key: string; label: string; items: SidebarItem[] }

/** What each workspace is called and drawn as in the sidebar's header (labels as the server's index names them). */
export const WORKSPACE_META: Record<WorkspaceKey, { label: string; sub: string; icon: LucideIcon }> = {
  PATIENT_ADVOCATE: { label: 'Patient Advocate', sub: 'Patient advocacy', icon: HeartPulse },
  UMED: { label: 'UMED', sub: 'UMED operations', icon: FileCheck2 },
  MANIFEST: { label: 'Manifest review', sub: 'Visits to leads', icon: ClipboardList },
  SCHEDULING: { label: 'Scheduling', sub: 'Visits and dispensing', icon: CalendarDays },
  FINANCE: { label: 'Finance', sub: 'Handoffs and accounts', icon: Landmark },
  BUILDINGS: { label: 'Buildings', sub: 'Building records', icon: Building2 },
  PROVIDER: { label: 'Provider', sub: 'Field workspace', icon: Stethoscope },
  DIALER: { label: 'Dialer', sub: 'The eMerge line', icon: Phone },
  RECORD_FLOW: { label: 'Record Flow', sub: 'Where records stand', icon: Workflow },
  REPORTING: { label: 'Reports', sub: 'Reporting', icon: BarChart3 },
  ADMIN: { label: 'Admin console', sub: 'Administration', icon: ShieldCheck },
};

const isOps = (k: WorkspaceKey): k is OpsWorkspaceKey => (OPS_WORKSPACES as readonly string[]).includes(k);

/** The sidebar's state, for a control that lives elsewhere in the shell. */
export function useWorkspaceSidebar(): SidebarContextProps {
  return useSidebar();
}

/** The collapse / open control, for the page header (beside the title). */
export function SidebarTrigger({ className = '' }: { className?: string }) {
  return <ShadcnSidebarTrigger className={className} />;
}

/* ── Workspace header / switcher ────────────────────────────────────── */

function WorkspaceFace({ workspace, label, sub }: { workspace: WorkspaceKey; label: string; sub?: string }) {
  const meta = WORKSPACE_META[workspace];
  const Icon = meta.icon;
  return (
    <>
      <span className="wsb-ws-tile grid size-8 shrink-0 place-items-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground shadow-sm" aria-hidden="true">
        <Icon className="size-[18px]" focusable="false" />
      </span>
      <span className="wsb-ws-text flex min-w-0 flex-col group-data-[collapsible=icon]:hidden">
        <span className="wsb-ws-name truncate text-sm font-semibold leading-tight">{label}</span>
        <span className="wsb-ws-sub truncate text-xs text-sidebar-foreground/75">{sub ?? meta.sub}</span>
      </span>
    </>
  );
}

const faceClass = 'wsb-ws flex w-full min-h-11 items-center gap-2.5 rounded-lg p-1.5 text-left text-sidebar-foreground group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0';

function WorkspaceSwitcher({ current, workspaces, onNavigate }: { current: OpsWorkspaceKey; workspaces: OpsWorkspace[]; onNavigate: () => void }) {
  const { collapsed } = useSidebar();
  const [open, setOpen] = useState(false);
  const listId = useId();
  const reduce = useReducedMotion();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const here = workspaces.find((w) => w.key === current) || { key: current, label: WORKSPACE_META[current].label, path: '' };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => { if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); buttonRef.current?.focus(); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true); };
  }, [open]);
  useEffect(() => { if (collapsed) setOpen(false); }, [collapsed]);

  // One workspace: nothing to switch to, so nothing pretends to be a menu.
  if (workspaces.length < 2) {
    return <div className={faceClass} data-single="true" title={collapsed ? here.label : undefined}><WorkspaceFace workspace={current} label={here.label} /></div>;
  }
  return (
    <div className="wsb-ws-wrap relative" ref={wrapRef}>
      <button ref={buttonRef} type="button"
        className={cn(faceClass, 'cursor-pointer outline-none ring-sidebar-ring hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-inset motion-safe:transition-colors')}
        aria-expanded={open} aria-controls={listId} aria-label={`Workspace: ${here.label}. Switch workspace`} onClick={() => setOpen((o) => !o)}>
        <WorkspaceFace workspace={current} label={here.label} />
        <ChevronsUpDown className="wsb-ws-chevron ml-auto size-4 shrink-0 text-sidebar-foreground/70 group-data-[collapsible=icon]:hidden" aria-hidden="true" focusable="false" />
      </button>
      {/* Enter-only motion: a closed list leaves at once, so nothing closed stays in the accessibility tree. */}
      {open && (
          <motion.ul id={listId} aria-label="Your operations workspaces"
            className={cn('wsb-ws-list absolute z-40 m-0 min-w-[200px] list-none rounded-lg border border-sidebar-border bg-popover p-1 text-popover-foreground shadow-lg',
              collapsed ? 'left-[calc(100%+8px)] top-0' : 'left-0 right-0 top-[calc(100%+4px)]')}
            initial={reduce ? false : { opacity: 0, y: -4, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.14, ease: 'easeOut' }}>
            {workspaces.map((w) => {
              const WIcon = WORKSPACE_META[w.key].icon;
              const isHere = w.key === current;
              return (
                <li key={w.key}>
                  <Link to={w.path} aria-current={isHere ? 'page' : undefined}
                    className="wsb-ws-option flex min-h-9 items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-popover-foreground no-underline outline-none ring-sidebar-ring hover:bg-sidebar-accent focus-visible:ring-2 focus-visible:ring-inset aria-[current=page]:font-semibold"
                    onClick={() => { setOpen(false); onNavigate(); }}>
                    <WIcon className="size-4 shrink-0" aria-hidden="true" focusable="false" />
                    <span>{w.label}</span>
                    {isHere && <Check className="ml-auto size-4 text-sidebar-primary" aria-hidden="true" focusable="false" />}
                  </Link>
                </li>
              );
            })}
          </motion.ul>
      )}
    </div>
  );
}

function OpsHeader({ current, onNavigate }: { current: OpsWorkspaceKey; onNavigate: () => void }) {
  const workspaces = useOpsWorkspaces(current);
  return <WorkspaceSwitcher current={current} workspaces={workspaces} onNavigate={onNavigate} />;
}

/* ── Body ───────────────────────────────────────────────────────────── */

const nf = new Intl.NumberFormat('en-US');

function SidebarBody({ workspace, navLabel, groups, footer, face }: { workspace: WorkspaceKey; navLabel: string; groups: SidebarGroup[]; footer?: SidebarGroup[]; face?: WorkspaceFaceName }) {
  const { collapsed, mobile, setOpenMobile } = useSidebar();
  const onNavigate = () => { if (mobile) setOpenMobile(false); };
  const base = useId();
  const renderGroups = (list: SidebarGroup[], prefix: string) => list.filter((g) => g.items.length > 0).map((g) => {
    const gid = `${base}-${prefix}-${g.key}`;
    return (
      <SidebarGroup key={g.key} className="wsb-group" role="group" aria-labelledby={gid}>
        <SidebarGroupLabel id={gid} className="wsb-group-label">{g.label}</SidebarGroupLabel>
        <SidebarMenu className="wsb-menu">
          {g.items.map((it) => {
            const Icon = it.icon;
            const counts = [it.badge, it.secondary].filter((b): b is SidebarBadge => Boolean(b));
            const said = counts.length ? counts.map((b) => b.label || nf.format(b.value)).join(', ') : null;
            const stopDot = counts.some((b) => b.tone === 'stop');
            return (
              <SidebarMenuItem key={it.key} className="wsb-item">
                <SidebarMenuButton asChild isActive={Boolean(it.active)} tooltip={`${it.label}${said ? ` · ${said}` : ''}`}>
                  <Link to={it.href} replace={it.replace} aria-current={it.active ? 'page' : undefined}
                    title={!collapsed ? it.title : undefined} aria-label={said ? `${it.label}, ${said}` : undefined}
                    onClick={() => { if (it.onSelect) it.onSelect(); onNavigate(); }}>
                    <span className="wsb-icon-box relative inline-grid size-5 shrink-0 place-items-center" aria-hidden="true">
                      <Icon className="wsb-icon" focusable="false" />
                      {counts.length > 0 && (
                        <span className={cn('wsb-dot absolute -right-1 -top-[3px] hidden size-[7px] rounded-full shadow-[0_0_0_2px_hsl(var(--sidebar-background))] group-data-[collapsible=icon]:block',
                          stopDot ? 'stop bg-[var(--tide-status-danger)]' : 'bg-sidebar-primary')} />
                      )}
                    </span>
                    <span className="wsb-label min-w-0 flex-1 truncate group-data-[collapsible=icon]:sr-only">{it.label}</span>
                    {counts.map((b, i) => (
                      <SidebarMenuBadge key={i} tone={b.tone} title={b.label}>{nf.format(b.value)}</SidebarMenuBadge>
                    ))}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroup>
    );
  });
  // One landmark for the whole sidebar (the workspace's name, its views and the footer doors), so nothing in it sits
  // outside a landmark.
  return (
    <nav className="wsb-nav-root flex flex-[1_0_auto] flex-col" aria-label={navLabel}>
      <SidebarHeader className="wsb-head">
        {isOps(workspace)
          ? <OpsHeader current={workspace} onNavigate={onNavigate} />
          : <div className={faceClass} data-single="true" title={collapsed ? (face?.label ?? WORKSPACE_META[workspace].label) : undefined}><WorkspaceFace workspace={workspace} label={face?.label ?? WORKSPACE_META[workspace].label} sub={face?.sub} /></div>}
      </SidebarHeader>
      <SidebarContent className="wsb-nav">
        {renderGroups(groups, 'g')}
      </SidebarContent>
      {/* The theme switch lives here because this footer is the one piece of furniture every workspace mounts (the
          top bar is missing from the Dialer and the admin console), and it follows the person, not the page. */}
      <SidebarFooter className="wsb-foot">
        {footer && footer.some((g) => g.items.length > 0) && renderGroups(footer, 'f')}
        {/* The admin console is light-only for now (components/theme), so it shows no switch. */}
        {workspace !== 'ADMIN' && <ThemeSwitch className="wsb-theme" />}
      </SidebarFooter>
    </nav>
  );
}

/* ── Layout ─────────────────────────────────────────────────────────── */

/** The name a workspace shows in the sidebar's header, when it is more specific than the workspace's own (Podiatry
 *  inside the Provider workspace). Not for the operations workspaces, whose header is the switcher. */
export interface WorkspaceFaceName { label: string; sub?: string }

export interface WorkspaceLayoutProps {
  /** The workspace this is (named in the sidebar's header; in the operations workspaces, marked in the switcher). */
  workspace: WorkspaceKey;
  /** The nav landmark's name ("UMED views"). */
  navLabel: string;
  groups: SidebarGroup[];
  /** Groups pinned to the foot of the sidebar (the admin console's doors). */
  footer?: SidebarGroup[];
  /** The header's name, when more specific than the workspace's (see WorkspaceFaceName). */
  face?: WorkspaceFaceName;
  className?: string;
  /** The page header and main. */
  children: React.ReactNode;
}

export function WorkspaceLayout({ workspace, navLabel, groups, footer, face, className, children }: WorkspaceLayoutProps) {
  return (
    <SidebarProvider className={className}>
      <Sidebar title={`${face?.label ?? WORKSPACE_META[workspace].label} navigation`}>
        <SidebarBody workspace={workspace} navLabel={navLabel} groups={groups} footer={footer} face={face} />
      </Sidebar>
      <SidebarInset>{children}</SidebarInset>
    </SidebarProvider>
  );
}

export default WorkspaceLayout;
